// dependencies
import { format } from "date-fns";
import React, { useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { faCalendarCheck } from "@fortawesome/pro-regular-svg-icons";
import { faCircle, faCircleXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Spinner } from "@performyard/py-components";

import VerticalExpander from "../../../../../../../components/AnimationWrappers/VerticalExpander";
import Button from "../../../../../../../components/Button";
// components
import Icon from "../../../../../../../components/Icon/Icon";
import Link from "../../../../../../../components/Link";
import Spacer from "../../../../../../../components/Spacer";
import Typography from "../../../../../../../components/Text";

// hooks
import {
	useGoalDetails,
	useGoalHistory,
	useMutateDeleteGoalHistory,
	useRbacObjectPermissions,
} from "../../../../../../../api/hooks";
import useWindowSize from "../../../../../../../hooks/useWindowSize";

// services
import { Goal, GoalHistory } from "../../../../../../../api/services";

// utils
import toEmployeeUrl from "../../../../../../../utils/URLs/Employee";
import { parseGoalPermissionsForUser } from "../../../../utils";
import { getGoalHistoryFormattedUpdate } from "../../utils";

// styles
import css from "../../ActivityStreamPanel.module.scss";

type HistoryListEntryProps = {
	entry: GoalHistory;
	onSelect?: () => void;
	showAsOf?: boolean;
};

const defaultClosedHeight = 640;

const HistoryListEntry = ({ entry, onSelect, showAsOf = false }: HistoryListEntryProps) => {
	const { goal_id: goalId } = useParams<{ goal_id: string }>();
	const formattedDate = showAsOf
		? format(entry?.as_of, "EEE, MMM d yyyy")
		: entry.created_at
			? format(entry.created_at, "EEE, MMM d yyyy")
			: null;
	const [isExpanded, setIsExpanded] = useState(false);
	const [, windowHeight = 100] = useWindowSize();
	const ref = useRef<HTMLDivElement>(null);
	const entryRef = useRef<HTMLDivElement>(null);
	const isSystemDeactivation = entry?.message === "Goal was archived by system due to assignee deactivation";
	const { data: permissions } = useRbacObjectPermissions("goal", goalId || "");

	const { userCanDeleteValueUpdate } = parseGoalPermissionsForUser(permissions);

	const { refetch: refetchActivity } = useGoalHistory({
		goalID: entry?.strategy || "",
		sortDirection: "desc",
		sortName: "_id",
	});

	const { refetch: refetchProgress } = useGoalHistory({
		goalID: entry?.strategy || "",
		sortDirection: "desc",
		sortName: "as_of",
		type: "progress",
	});

	const { data, refetch: refetchGoal } = useGoalDetails({
		goalID: entry?.strategy,
	});
	const goal = data?.goal;

	const { mutate, isPending } = useMutateDeleteGoalHistory({
		onSuccess: () => {
			refetchActivity();
			refetchProgress();
			refetchGoal();
		},
	});

	const canDelete =
		(!!entry?.change?.value || entry?.change?.value === 0) && userCanDeleteValueUpdate && entry.action !== "create";

	//rounded down windowHeight*0.6 to the nearest whole number that's divisible by the line height of 16
	const roundedWindowHeightOverride = Math.floor((windowHeight * 0.6) / 16) * 16;
	const closedHeight = (windowHeight > defaultClosedHeight ? defaultClosedHeight : roundedWindowHeightOverride) + 4;

	const height = ref?.current?.clientHeight || 0;

	const dateHeader =
		onSelect && goal?.status === "active" ? (
			<div className={css.editableHistory}>
				<Typography weight="regular" size="caption">
					{showAsOf ? "As of: " : ""}
					{formattedDate}
				</Typography>
				<Icon
					size="x-small"
					color="neutral-9"
					transform={{ size: 4 }}
					icon={faCircle}
					className={css.seperator}
				/>
				<button onClick={onSelect} className={css.calendar}>
					<Icon size="small" icon={faCalendarCheck} testid={faCalendarCheck?.iconName} />
				</button>
			</div>
		) : (
			<div>
				<Typography weight="regular" size="caption">
					{formattedDate}
				</Typography>
			</div>
		);
	return (
		<div key={entry._id} className={css.entry} ref={entryRef} data-testid="history-list-entry">
			<div>
				{dateHeader}
				<Spacer height={1} />
				<VerticalExpander isExpanded={isExpanded} closedHeight={closedHeight}>
					<div ref={ref} data-testid="history-list-entry-content">
						{isSystemDeactivation ? (
							<Typography
								element="span"
								weight="regular"
								size="paragraph-3"
								data-testid="history-list-entry-message"
							>
								{entry?.message}.
							</Typography>
						) : (
							<>
								{entry?.actor_api_token?.length ? (
									<Typography
										element="span"
										weight="semi-bold"
										size="paragraph-3"
										color="neutral-9"
										data-testid={`history-list-entry-actor-name-${(entry?.actor?.name || "").replace(/\s+/g, "-")}`}
									>
										{entry?.actor?.name}{" "}
									</Typography>
								) : (
									<Link
										href={toEmployeeUrl(entry?.actor?._id)}
										className={css.employeeLink}
										forceHardReload={true}
									>
										<Typography
											element="span"
											weight="semi-bold"
											size="paragraph-3"
											color="neutral-9"
											data-testid={`history-list-entry-actor-name-${(entry?.actor?.name || "").replace(/\s+/g, "-")}`}
										>
											{entry?.actor?.name}{" "}
										</Typography>
									</Link>
								)}
								<Typography element="span" weight="regular" size="paragraph-3">
									{getGoalHistoryFormattedUpdate(entry, goal as Goal)}
								</Typography>
								{entry?.message ? (
									<Typography
										element="span"
										weight="regular"
										size="paragraph-3"
										data-testid="history-list-entry-message"
									>
										{" "}
										and said &quot;
										{entry?.message}&quot;.
									</Typography>
								) : (
									<Typography element="span" weight="regular" size="paragraph-3">
										.{" "}
									</Typography>
								)}
							</>
						)}
						{canDelete ? (
							<Typography element="span" weight="regular" size="paragraph-3">
								<button
									onClick={() =>
										mutate({
											goalID: entry?.strategy || "",
											goalHistoryID: entry?._id,
										})
									}
									className={css.iconButton}
									data-testid="delete-goal-history-button"
								>
									{isPending ? <Spinner size={12} /> : <FontAwesomeIcon icon={faCircleXmark} />}
								</button>
							</Typography>
						) : null}
					</div>
				</VerticalExpander>
				{height > closedHeight ? (
					<div>
						<Spacer height={1} />
						<Button onClick={() => setIsExpanded(!isExpanded)} isHollow={true} size="micro">
							{isExpanded ? "Read Less" : "Read More"}
						</Button>
					</div>
				) : null}
			</div>
		</div>
	);
};

export default HistoryListEntry;
